import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, AlertCircle, BarChart3, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportHeader } from '@/components/report/report-header';
import { ReportKpis } from '@/components/report/report-kpis';
import { ReportSegments } from '@/components/report/report-segments';
import { ReportSkuTable } from '@/components/report/report-sku-table';
import { ReportTrendChart } from '@/components/report/report-trend-chart';
import { ReportTopSkusChart } from '@/components/report/report-top-skus-chart';
import { ReportAutoCompare } from '@/components/report/report-auto-compare';
import { useCurrentDate } from '@/hooks/use-current-date';
import { getSharedReport } from '@/lib/api';
import type { SharedReport } from '@/lib/api';

const FOOTER_DATE = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const EXPIRES_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

function formatMonthRange(months: string[]): string | undefined {
  if (months.length < 2) return months[0];
  const first = months[0];
  const last = months[months.length - 1];
  if (!first || !last) return undefined;
  const fmt = (m: string) => {
    const [y, mo] = m.split('-');
    return `${mo}/${y}`;
  };
  return `${fmt(first)} → ${fmt(last)}`;
}

export default function SharedReportRoute() {
  const { token } = useParams<{ token: string }>();
  const [report, setReport] = useState<SharedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const now = useCurrentDate();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getSharedReport(token)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        document.title = `Báo cáo ${data.brand} — GearVN`;
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      document.title = 'Monitor Dashboard — GearVN';
    };
  }, [token]);

  const focalSkus = useMemo(
    () =>
      (report?.monitors ?? [])
        .filter((m) => m.is_focal === 1)
        .sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [report]
  );

  const allSkus = useMemo(
    () =>
      [...(report?.monitors ?? [])].sort((a, b) => {
        if (a.is_focal !== b.is_focal) return b.is_focal - a.is_focal;
        return a.name.localeCompare(b.name, 'vi');
      }),
    [report]
  );

  const monthRange = useMemo(
    () => (report ? formatMonthRange(report.months) : undefined),
    [report]
  );

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <div className="flex items-end justify-between border-b pb-4">
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-72" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-12 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-lg font-semibold">Link không khả dụng</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? 'Link chia sẻ này đã hết hạn hoặc đã bị thu hồi.'}
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Vui lòng liên hệ GearVN để được cấp link mới.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-5xl">
      <div className="print-watermark hidden">CONFIDENTIAL</div>

      {/* Top action bar — only Print + share info, no nav */}
      <div className="sticky top-0 z-20 flex flex-col gap-2 border-b bg-background/80 px-6 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" />
          <span>
            Link chia sẻ —{' '}
            {report.share.expires_at
              ? `hết hạn ${EXPIRES_FMT.format(new Date(report.share.expires_at))}`
              : 'không hết hạn'}
          </span>
        </div>
        <Button size="sm" onClick={handlePrint}>
          <Printer className="h-4 w-4" />
          In / Xuất PDF
        </Button>
      </div>

      <div className="space-y-6 p-6">
        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div className="text-muted-foreground">{error}</div>
          </div>
        )}

        <ReportHeader brand={report.brand} monthRange={monthRange} date={now} />

        <ReportKpis totals={report.totals} periodLabel={report.period.label} compare={null} />

        <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <BarChart3 className="h-4 w-4 text-primary" />
                {report.months.length === 1
                  ? `Top SKU theo doanh thu — ${report.period.label}`
                  : 'Xu hướng Doanh thu & Lợi nhuận'}
              </h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {report.months.length === 1
                  ? `${report.brand} · sắp xếp doanh thu giảm dần`
                  : `${report.months.length} tháng gần nhất · ${report.brand}`}
              </p>
            </div>
          </div>
          {report.months.length === 1 ? (
            <ReportTopSkusChart monitors={report.monitors} />
          ) : (
            <ReportTrendChart data={report.trend} />
          )}
        </section>

        {report.auto_compare && <ReportAutoCompare data={report.auto_compare} />}

        <ReportSegments segments={report.segments} />

        <div className="print-break-before pt-2">
          <ReportSkuTable title="Sản phẩm Tập trung (Focal Point)" monitors={focalSkus} />
        </div>

        <ReportSkuTable title="Toàn bộ SKU Active" monitors={allSkus} />

        <footer className="flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
          <span>
            GearVN × {report.brand} — Confidential | {FOOTER_DATE.format(now)}
          </span>
          <span>{report.total_skus} SKUs · {report.focal_count} focal</span>
        </footer>
      </div>
    </div>
  );
}
